// Demo story: route runs for the last 60 days plus today. Past days run every
// route scheduled for that weekday (2-4 per day), all completed. Today always
// has 4: one in progress (started 2h ago, 2 of 4 stops done), two completed,
// one pending.

import { ids, type LatLng } from '../constants';
import { DAY_MS, HOUR_MS, daysAgo, hoursAgo, iso, isoDate, rngFor, startOfToday, type Rng } from '../prng';
import { DEMO_ROUTES, DEMO_WEEKDAYS, polylineKm, truckById, type DemoCollectionRoute } from './fleet';

export type DemoRouteRunStatus = 'pending' | 'in_progress' | 'completed';

export interface DemoRouteRunStop {
  collection_point_id: string;
  seq: number;
  status: 'pending' | 'completed';
  arrived_at: string | null;
  collected_kg: number | null;
}

export interface DemoRouteRun {
  id: string;
  route_id: string;
  truck_id: string;
  driver_id: string | null;
  /** 'YYYY-MM-DD' */
  run_date: string;
  status: DemoRouteRunStatus;
  scheduled_start_at: string;
  /** null while the run is pending. */
  started_at: string | null;
  finished_at: string | null;
  stops: DemoRouteRunStop[];
  /** Copy of the route geometry, [lng, lat][]. */
  geometry: Array<[number, number]>;
  distance_km: number;
}

export const ROUTE_RUN_HISTORY_DAYS = 60;

/** Riyadh/Jeddah are UTC+3 all year; schedule times are local. */
const KSA_UTC_OFFSET_H = 3;
const AVG_SPEED_KMH = 28;
const SERVICE_MIN_PER_STOP = 15;

/** Hours to drive each leg of the route geometry (road ~1.3x straight line). */
function legHours(route: DemoCollectionRoute): number[] {
  const out: number[] = [];
  for (let i = 1; i < route.geometry.length; i += 1) {
    out.push((polylineKm([route.geometry[i - 1], route.geometry[i]]) * 1.3) / AVG_SPEED_KMH);
  }
  return out;
}

function scheduledStart(day: Date, route: DemoCollectionRoute): Date {
  const [hh, mm] = route.schedule.start_time.split(':').map(Number);
  return new Date(day.getTime() + (hh + mm / 60 - KSA_UTC_OFFSET_H) * HOUR_MS);
}

function runId(day: Date, routeIndex: number): string {
  // Stable per (date, route) so history keeps its ids as days pass.
  return ids.routeRun(Math.floor(day.getTime() / DAY_MS) * 8 + routeIndex);
}

/** Build a run; `doneStops` stops are completed (all of them for a completed run). */
function buildRun(
  rng: Rng,
  day: Date,
  routeIndex: number,
  status: DemoRouteRunStatus,
  scheduled: Date,
  start: Date,
  doneStops: number,
): DemoRouteRun {
  const route = DEMO_ROUTES[routeIndex];
  const legs = legHours(route);
  let t = start.getTime();
  const stops: DemoRouteRunStop[] = route.stops.map((stop, i) => {
    t += legs[i] * HOUR_MS * rng.float(0.9, 1.25, 3);
    const done = i < doneStops;
    const arrived = done ? new Date(t) : null;
    t += SERVICE_MIN_PER_STOP * 60000 * rng.float(0.8, 1.4, 3);
    return {
      collection_point_id: stop.collection_point_id,
      seq: stop.seq,
      status: done ? 'completed' : 'pending',
      arrived_at: arrived ? iso(arrived) : null,
      collected_kg: done ? rng.int(120, 950) : null,
    };
  });
  t += legs[legs.length - 1] * HOUR_MS * rng.float(0.9, 1.2, 3);
  return {
    id: runId(day, routeIndex),
    route_id: route.id,
    truck_id: route.truck_id,
    driver_id: truckById(route.truck_id)?.driver_id ?? null,
    run_date: isoDate(day),
    status,
    scheduled_start_at: iso(scheduled),
    started_at: status === 'pending' ? null : iso(start),
    finished_at: status === 'completed' ? iso(new Date(t)) : null,
    stops,
    geometry: route.geometry.map((p): [number, number] => [p[0], p[1]]),
    distance_km: route.distance_km,
  };
}

const RETAIL_LOOP = 2; // route 3, truck 3 (the one in transit)
const NORTH_LOOP = 0; // route 1
const JEDDAH_LOOP = 4; // route 5
const HEALTHCARE_RUN = 3; // route 4

/** Fresh array of route runs, oldest first. */
export function generateRouteRuns(): DemoRouteRun[] {
  const runs: DemoRouteRun[] = [];
  for (let d = ROUTE_RUN_HISTORY_DAYS; d >= 1; d -= 1) {
    const day = daysAgo(d);
    // One stream per calendar date: a day's history never changes as time passes.
    const rng = rngFor(`route-runs:${isoDate(day)}`);
    const weekday = DEMO_WEEKDAYS[day.getUTCDay()];
    DEMO_ROUTES.forEach((route, idx) => {
      if (!route.schedule.days.includes(weekday)) return;
      const scheduled = scheduledStart(day, route);
      const start = new Date(scheduled.getTime() + rng.int(-10, 20) * 60000);
      runs.push(buildRun(rng, day, idx, 'completed', scheduled, start, route.stops.length));
    });
  }

  // Today is anchored to "now" (not the weekday schedule) so the live view always has the same shape.
  const today = startOfToday();
  const rng = rngFor('route-runs:today');
  const northStart = hoursAgo(7);
  const jeddahStart = hoursAgo(5.5);
  const liveStart = hoursAgo(2);
  const pendingStart = hoursAgo(-1.5);
  const live = buildRun(rng, today, RETAIL_LOOP, 'in_progress', liveStart, liveStart, 2);
  // Keep the in-progress run believable: stop 1 ~80 min ago, stop 2 ~25 min ago.
  live.stops[0].arrived_at = iso(hoursAgo(1.3));
  live.stops[1].arrived_at = iso(hoursAgo(0.4));
  runs.push(
    buildRun(rng, today, NORTH_LOOP, 'completed', northStart, northStart, DEMO_ROUTES[NORTH_LOOP].stops.length),
    buildRun(rng, today, JEDDAH_LOOP, 'completed', jeddahStart, jeddahStart, DEMO_ROUTES[JEDDAH_LOOP].stops.length),
    live,
    buildRun(rng, today, HEALTHCARE_RUN, 'pending', pendingStart, pendingStart, 0),
  );
  return runs;
}

/** Point at `fraction` (0..1) of the polyline's length. */
export function pointAlong(geometry: Array<[number, number]>, fraction: number): LatLng {
  if (geometry.length === 0) return { lat: 0, lng: 0 };
  const f = Math.min(1, Math.max(0, fraction));
  const total = polylineKm(geometry);
  if (total === 0 || geometry.length === 1) return { lat: geometry[0][1], lng: geometry[0][0] };
  let remaining = total * f;
  for (let i = 1; i < geometry.length; i += 1) {
    const seg = polylineKm([geometry[i - 1], geometry[i]]);
    if (remaining <= seg || i === geometry.length - 1) {
      const t = seg === 0 ? 0 : Math.min(1, remaining / seg);
      const [lng0, lat0] = geometry[i - 1];
      const [lng1, lat1] = geometry[i];
      return { lat: lat0 + (lat1 - lat0) * t, lng: lng0 + (lng1 - lng0) * t };
    }
    remaining -= seg;
  }
  const last = geometry[geometry.length - 1];
  return { lat: last[1], lng: last[0] };
}

/**
 * Where the run's truck is now: at the depot for pending/completed runs; for an
 * in-progress run, between the last completed stop and the next one, advanced
 * by the time elapsed since the last arrival (so polling shows movement).
 */
export function runPosition(run: DemoRouteRun, now: Date = new Date()): LatLng {
  const g = run.geometry;
  if (g.length === 0) return { lat: 0, lng: 0 };
  if (run.status !== 'in_progress') {
    const p = run.status === 'completed' ? g[g.length - 1] : g[0];
    return { lat: p[1], lng: p[0] };
  }
  const done = run.stops.filter((s) => s.status === 'completed').length;
  const from = g[Math.min(done, g.length - 1)];
  const to = g[Math.min(done + 1, g.length - 1)];
  const lastArrival = done > 0 ? run.stops[done - 1].arrived_at : run.started_at;
  const legH = Math.max((polylineKm([from, to]) * 1.3) / AVG_SPEED_KMH, 0.1);
  const elapsedH = lastArrival ? (now.getTime() - Date.parse(lastArrival)) / HOUR_MS - SERVICE_MIN_PER_STOP / 60 : 0;
  const t = Math.min(0.92, Math.max(0.05, elapsedH / legH));
  return { lat: from[1] + (to[1] - from[1]) * t, lng: from[0] + (to[0] - from[0]) * t };
}
